/**
 * Diffing service for comparing document versions
 */

import DiffMatchPatch from 'diff-match-patch';

const textDiffer = new DiffMatchPatch();


/**
 * Perform diff on specification content
 * @param {string} oldHtml - Old HTML content
 * @param {string} newHtml - New HTML content
 * @returns {Object} Diff result with full document HTML
 */
export function diffSpecContent(oldHtml, newHtml) {
    try {
        // Clean up HTML to handle empty content
        oldHtml = (oldHtml || '').trim();
        newHtml = (newHtml || '').trim();
        
        // Generate full document with inline changes
        const { html, hasChanges } = generateFullDocumentDiff(oldHtml, newHtml);
        
        return {
            fullDocumentHtml: html,
            unchanged: !hasChanges
        };
    } catch (error) {
        console.error('Error calculating spec diff:', error);
        return { fullDocumentHtml: '<p>Error generating diff</p>', unchanged: true, error: true };
    }
}

/**
 * Format structural changes for display
 * @param {Object} oldDoc - Old document structure
 * @param {Object} newDoc - New document structure
 * @param {Object} delta - jsondiffpatch delta
 * @returns {Array} Formatted changes
 */
function _formatStructuralChanges(oldDoc, newDoc, delta) {
    const changes = [];
    
    // Process content changes
    if (delta.content) {
        processContentChanges(delta.content, oldDoc.content, newDoc.content, changes);
    }
    
    return changes;
}

/**
 * Process content array changes
 * 
 * This function interprets the jsondiffpatch delta format for arrays:
 * - [newValue] = addition
 * - [oldValue, 0, 0] = deletion
 * - [oldValue, newValue] = modification
 * - {nested} = complex nested changes
 * 
 * @param {Object} contentDelta - Delta for content array
 * @param {Array} oldContent - Old content array
 * @param {Array} newContent - New content array
 * @param {Array} changes - Changes array to populate
 */
function processContentChanges(contentDelta, oldContent = [], newContent = [], changes) {
    // Handle array modifications
    Object.keys(contentDelta).forEach(key => {
        if (key === '_t') return; // Skip array type marker
        
        const change = contentDelta[key];
        const index = parseInt(key.replace('_', ''));
        
        if (Array.isArray(change)) {
            if (change.length === 1) {
                // Addition
                changes.push({
                    type: 'added',
                    content: newContent[index],
                    index
                });
            } else if (change.length === 3 && change[2] === 0) {
                // Deletion
                changes.push({
                    type: 'removed',
                    content: oldContent[index],
                    index
                });
            } else if (change.length === 2) {
                // Modification - text only change
                changes.push({
                    type: 'modified',
                    oldContent: oldContent[index],
                    newContent: newContent[index],
                    node: newContent[index], // Include the node for rendering
                    index
                });
            }
        } else if (typeof change === 'object') {
            // Nested changes (including attribute or content changes)
            const oldNode = oldContent[index];
            const newNode = newContent[index];
            
            if (oldNode && newNode) {
                // Determine if this is a text content change or attribute change
                const isTextChange = change.content && !change.attrs;
                
                changes.push({
                    type: 'modified',
                    oldContent: oldNode,
                    newContent: newNode,
                    node: newNode, // Use the new node for rendering
                    index,
                    details: change,
                    isTextChange
                });
            }
        }
    });
}

/**
 * Perform line-by-line text diff like VS Code
 * @param {string} oldText - Old text
 * @param {string} newText - New text
 * @returns {Array} Array of line diff operations
 */
export function diffText(oldText, newText) {
    try {
        const oldLines = oldText.split('\n');
        const newLines = newText.split('\n');
    
    // Use diff-match-patch on line array to get line-level diffs
    textDiffer.Diff_Timeout = 2.0;
    
    // Convert lines to a format diff-match-patch can use
    // We'll use a character mapping approach where each line gets a unique character
    const lineMap = new Map();
    const reverseMap = new Map();
    let charCode = 1; // Start at 1 to avoid null character
    
    // Build the mapping
    const allLines = [...new Set([...oldLines, ...newLines])];
    allLines.forEach(line => {
        if (!lineMap.has(line)) {
            const char = String.fromCharCode(charCode++);
            lineMap.set(line, char);
            reverseMap.set(char, line);
        }
    });
    
    // Convert line arrays to strings using the mapping
    const oldString = oldLines.map(line => lineMap.get(line)).join('');
    const newString = newLines.map(line => lineMap.get(line)).join('');
    
    // Perform diff on the mapped strings
    const diffs = textDiffer.diff_main(oldString, newString);
    textDiffer.diff_cleanupSemantic(diffs);
    
    // Convert back to line diffs
    const result = [];
    let oldLineNum = 0;
    let newLineNum = 0;
    
    diffs.forEach(([operation, chars]) => {
        const lines = chars.split('').map(char => reverseMap.get(char));
        
        if (operation === 0) { // Equal
            // Skip unchanged lines - we only want to show changes
            oldLineNum += lines.length;
            newLineNum += lines.length;
        } else if (operation === -1) { // Delete
            lines.forEach(line => {
                result.push({
                    type: 'removed',
                    oldLineNumber: oldLineNum + 1,
                    content: line
                });
                oldLineNum++;
            });
        } else if (operation === 1) { // Insert
            lines.forEach(line => {
                result.push({
                    type: 'added',
                    newLineNumber: newLineNum + 1,
                    content: line
                });
                newLineNum++;
            });
        }
    });
    
    // Group consecutive changes and detect modifications
    const groupedResult = [];
    let i = 0;
    
    while (i < result.length) {
        const current = result[i];
        
        // Check if this is a modification (removed followed by added)
        if (current.type === 'removed' && 
            i + 1 < result.length && 
            result[i + 1].type === 'added') {
            
            // This is a modified line - compute inline diff
            const oldLine = current.content;
            const newLine = result[i + 1].content;
            const inlineDiff = computeInlineDiff(oldLine, newLine);
            
            groupedResult.push({
                type: 'modified',
                oldLineNumber: current.oldLineNumber,
                newLineNumber: result[i + 1].newLineNumber,
                oldContent: oldLine,
                newContent: newLine,
                inlineDiff
            });
            
            i += 2; // Skip both lines
        } else {
            // Just a simple add or remove
            groupedResult.push(current);
            i++;
        }
    }
    
    // Add grouping information
    return addGroupingInfo(groupedResult);
    } catch (error) {
        console.error('Error calculating text diff:', error);
        return [];
    }
}

/**
 * Add grouping information to changes
 * @param {Array} changes - Array of changes
 * @returns {Array} Changes with grouping info
 */
function addGroupingInfo(changes) {
    if (changes.length === 0) return changes;
    
    const groupedChanges = [];
    let lastLineNum = null;
    
    changes.forEach((change) => {
        const currentLineNum = change.oldLineNumber || change.newLineNumber;
        
        // Add separator if there's a gap in line numbers
        if (lastLineNum !== null && currentLineNum - lastLineNum > 1) {
            change.needsSeparator = true;
        }
        
        groupedChanges.push(change);
        lastLineNum = currentLineNum;
    });
    
    return groupedChanges;
}

/**
 * Compute inline diff for modified lines
 * @param {string} oldLine - Old line content
 * @param {string} newLine - New line content
 * @returns {Array} Inline diff segments
 */
function computeInlineDiff(oldLine, newLine) {
    const diffs = textDiffer.diff_main(oldLine, newLine);
    textDiffer.diff_cleanupSemantic(diffs);
    
    return diffs.map(([operation, text]) => ({
        type: operation === 0 ? 'equal' : operation === 1 ? 'insert' : 'delete',
        text
    }));
}

/**
 * Generate full document diff with inline changes
 * @param {string} oldHtml - Old HTML content
 * @param {string} newHtml - New HTML content
 * @returns {Object} Object with full HTML and change status
 */
function generateFullDocumentDiff(oldHtml, newHtml) {
    // Handle edge cases
    if (!oldHtml && !newHtml) {
        return { html: '', hasChanges: false };
    }
    
    if (!oldHtml && newHtml) {
        const div = document.createElement('div');
        div.innerHTML = newHtml;
        addParagraphNumbersAsText(div);
        markAllAsAdded(div);
        return { html: div.innerHTML, hasChanges: true };
    }
    
    if (oldHtml && !newHtml) {
        const div = document.createElement('div');
        div.innerHTML = oldHtml;
        addParagraphNumbersAsText(div);
        markAllAsDeleted(div);
        return { html: div.innerHTML, hasChanges: true };
    }
    
    // Parse HTML and add paragraph numbers
    const oldDiv = document.createElement('div');
    oldDiv.innerHTML = oldHtml;
    const newDiv = document.createElement('div');
    newDiv.innerHTML = newHtml;
    
    addParagraphNumbersAsText(oldDiv);
    addParagraphNumbersAsText(newDiv);
    
    // Extract blocks for comparison
    const oldBlocks = Array.from(oldDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote'));
    const newBlocks = Array.from(newDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote'));
    
    // Strip paragraph numbers for content comparison
    const stripParagraphNumber = (text) => {
        return text.replace(/^\s*\[\d{4}\]\s*/, '').trim();
    };
    
    const oldTexts = oldBlocks.map(b => stripParagraphNumber(b.textContent.trim()));
    const newTexts = newBlocks.map(b => stripParagraphNumber(b.textContent.trim()));
    
    // Build the diff result
    const resultDiv = document.createElement('div');
    let hasChanges = false;
    
    // Track block mappings between old and new
    const oldToNewMap = new Map();
    const newToOldMap = new Map();
    const usedOldIndices = new Set();
    
    // Match identical content blocks (even if reordered)
    newTexts.forEach((newText, newIdx) => {
        const oldIdx = oldTexts.findIndex((oldText, idx) => 
            oldText === newText && !usedOldIndices.has(idx)
        );
        
        if (oldIdx !== -1) {
            oldToNewMap.set(oldIdx, newIdx);
            newToOldMap.set(newIdx, oldIdx);
            usedOldIndices.add(oldIdx);
        }
    });
    
    // Find deletions and additions that are likely modifications
    // Use fuzzy matching to pair deletions with additions
    const deletedIndices = [];
    const addedIndices = [];
    
    for (let i = 0; i < oldTexts.length; i++) {
        if (!oldToNewMap.has(i)) {
            deletedIndices.push(i);
        }
    }
    
    for (let i = 0; i < newTexts.length; i++) {
        if (!newToOldMap.has(i)) {
            addedIndices.push(i);
        }
    }
    
    // Create a map of likely modifications (deletion -> addition pairs) with inline diffs
    const modificationPairs = new Map();
    const usedAdditions = new Set();
    
    // For each deletion, find the best matching addition using fuzzy match
    deletedIndices.forEach(delIdx => {
        const deletedText = oldTexts[delIdx];
        let bestMatch = null;
        let bestScore = 0;
        let bestDiffs = null;
        
        addedIndices.forEach(addIdx => {
            if (usedAdditions.has(addIdx)) return;
            
            const addedText = newTexts[addIdx];
            // Calculate similarity score using diff-match-patch
            const diffs = textDiffer.diff_main(deletedText, addedText);
            textDiffer.diff_cleanupSemantic(diffs);
            
            // Calculate similarity as ratio of unchanged characters
            let unchangedChars = 0;
            let totalChars = Math.max(deletedText.length, addedText.length);
            
            diffs.forEach(([operation, text]) => {
                if (operation === 0) { // Equal
                    unchangedChars += text.length;
                }
            });
            
            const similarity = totalChars > 0 ? unchangedChars / totalChars : 0;
            
            // Consider it a modification if similarity is above threshold (e.g., 30%)
            // and it's the best match so far
            if (similarity > 0.3 && similarity > bestScore) {
                bestMatch = addIdx;
                bestScore = similarity;
                bestDiffs = diffs;
            }
        });
        
        if (bestMatch !== null) {
            modificationPairs.set(delIdx, {
                addIdx: bestMatch,
                diffs: bestDiffs
            });
            usedAdditions.add(bestMatch);
        }
    });
    
    // Process blocks in order
    const processedOldIndices = new Set();
    const processedNewIndices = new Set();
    
    // First, process all blocks in the new document order
    for (let newIdx = 0; newIdx < newTexts.length; newIdx++) {
        if (newToOldMap.has(newIdx)) {
            // This is an unchanged block
            const block = newBlocks[newIdx].cloneNode(true);
            resultDiv.appendChild(block);
            processedNewIndices.add(newIdx);
        } else if (!processedNewIndices.has(newIdx)) {
            // This is an addition - but check if it's part of a modification pair
            let isModification = false;
            
            // Check if this addition is paired with a deletion
            for (const [delIdx, pairInfo] of modificationPairs.entries()) {
                if (pairInfo.addIdx === newIdx && !processedOldIndices.has(delIdx)) {
                    // This is a modification - show deletion first, then addition
                    hasChanges = true;
                    
                    // Add the deletion with inline diff highlighting
                    const deletedBlock = oldBlocks[delIdx].cloneNode(true);
                    wrapInDeletion(deletedBlock, pairInfo.diffs);
                    resultDiv.appendChild(deletedBlock);
                    processedOldIndices.add(delIdx);
                    
                    // Add the addition with inline diff highlighting
                    const addedBlock = newBlocks[newIdx].cloneNode(true);
                    wrapInAddition(addedBlock, pairInfo.diffs);
                    resultDiv.appendChild(addedBlock);
                    processedNewIndices.add(newIdx);
                    
                    isModification = true;
                    break;
                }
            }
            
            if (!isModification) {
                // Pure addition (not part of a modification)
                hasChanges = true;
                const block = newBlocks[newIdx].cloneNode(true);
                wrapInAddition(block);
                resultDiv.appendChild(block);
                processedNewIndices.add(newIdx);
            }
        }
    }
    
    // Finally, add any remaining deletions that weren't part of modifications
    for (let oldIdx = 0; oldIdx < oldTexts.length; oldIdx++) {
        if (!oldToNewMap.has(oldIdx) && !processedOldIndices.has(oldIdx)) {
            hasChanges = true;
            const block = oldBlocks[oldIdx].cloneNode(true);
            wrapInDeletion(block);
            resultDiv.appendChild(block);
            processedOldIndices.add(oldIdx);
        }
    }
    
    return { html: resultDiv.innerHTML, hasChanges };
}

/**
 * Add paragraph numbers as actual text content to all paragraphs in a div
 * @param {Element} div - Container element
 */
function addParagraphNumbersAsText(div) {
    let paragraphCount = 0;
    
    // Find all paragraphs and headings
    const blocks = div.querySelectorAll('p, h1, h2, h3, h4, h5, h6');
    
    blocks.forEach(block => {
        // Skip headings - they don't get paragraph numbers
        if (block.tagName.toLowerCase().startsWith('h')) {
            return;
        }
        
        // Skip empty paragraphs
        const currentText = block.textContent.trim();
        if (currentText === '') {
            return;
        }
        
        // Skip if it's just a slash (block menu trigger)
        if (currentText === '/') {
            return;
        }
        
        // Check if paragraph number already exists
        // Look for pattern like [0001] at the beginning
        if (/^\[\d{4}\]/.test(currentText)) {
            // Paragraph number already exists, don't add another
            return;
        }
        
        // Increment paragraph count
        paragraphCount++;
        
        // Format the number with leading zeros
        const paddedNumber = paragraphCount.toString().padStart(4, '0');
        const paragraphNumber = `[${paddedNumber}]`;
        
        // Create a span for the paragraph number
        const numberSpan = document.createElement('span');
        numberSpan.className = 'paragraph-number';
        numberSpan.textContent = paragraphNumber + ' ';
        numberSpan.style.color = 'var(--color-text-tertiary)';
        numberSpan.style.fontFamily = 'var(--font-family-mono)';
        numberSpan.style.fontSize = 'var(--font-size-xs)';
        numberSpan.style.opacity = '0.6';
        numberSpan.style.userSelect = 'none';
        numberSpan.style.marginRight = '8px';
        
        // Insert the number span at the beginning of the block
        block.insertBefore(numberSpan, block.firstChild);
    });
}


/**
 * Mark all content in element as added
 * @param {Element} element - DOM element
 */
function markAllAsAdded(element) {
    const blocks = element.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote');
    blocks.forEach(block => {
        wrapInAddition(block);
    });
}

/**
 * Mark all content in element as deleted
 * @param {Element} element - DOM element
 */
function markAllAsDeleted(element) {
    const blocks = element.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote');
    blocks.forEach(block => {
        wrapInDeletion(block);
    });
}

/**
 * Wrap block in addition markup
 * @param {Element} block - Block element
 * @param {Array} inlineDiff - Optional inline diff segments
 */
function wrapInAddition(block, inlineDiff = null) {
    // Use CSS variables for theme-aware colors
    const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
    block.style.backgroundColor = isDarkTheme ? 'var(--color-collab-added-bg-dark)' : 'var(--color-collab-added-bg)';
    block.style.borderLeft = `3px solid ${isDarkTheme ? 'var(--color-collab-added-text-dark)' : 'var(--color-collab-added-text)'}`;
    block.style.paddingLeft = '12px';
    block.classList.add('diff-added');
    
    if (inlineDiff) {
        applyInlineDiff(block, inlineDiff, 'added');
    }
}

/**
 * Wrap block in deletion markup
 * @param {Element} block - Block element
 * @param {Array} inlineDiff - Optional inline diff segments
 */
function wrapInDeletion(block, inlineDiff = null) {
    // Use CSS variables for theme-aware colors
    const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
    block.style.backgroundColor = isDarkTheme ? 'var(--color-collab-removed-bg-dark)' : 'var(--color-collab-removed-bg)';
    block.style.borderLeft = `3px solid ${isDarkTheme ? 'var(--color-collab-removed-text-dark)' : 'var(--color-collab-removed-text)'}`;
    block.style.paddingLeft = '12px';
    block.style.textDecoration = 'line-through';
    block.style.opacity = '0.7';
    block.classList.add('diff-deleted');
    
    if (inlineDiff) {
        applyInlineDiff(block, inlineDiff, 'deleted');
    }
}

/**
 * Apply inline diff highlighting to a block
 * @param {Element} block - Block element
 * @param {Array} inlineDiff - Diff segments from diff-match-patch
 * @param {string} blockType - 'added' or 'deleted'
 */
function applyInlineDiff(block, inlineDiff, blockType) {
    // Skip if the block has a paragraph number span (handle it separately)
    const _paragraphNumberSpan = block.querySelector('.paragraph-number');
    
    // Find all text nodes in the block (excluding paragraph numbers)
    const walker = document.createTreeWalker(
        block,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: function(node) {
                // Skip text nodes that are inside paragraph number spans
                if (node.parentElement && node.parentElement.className === 'paragraph-number') {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        },
        false
    );
    
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
        textNodes.push(node);
    }
    
    if (textNodes.length === 0) return;
    
    // For simplicity, work with the first text node
    // In a more complex implementation, we'd handle multiple text nodes
    const textNode = textNodes[0];
    const parent = textNode.parentNode;
    
    // Create new nodes based on the diff
    const fragment = document.createDocumentFragment();
    
    inlineDiff.forEach(([operation, text]) => {
        if (operation === 1 && blockType === 'added') {
            // Added text in an addition block - make it bold/highlighted
            const span = document.createElement('span');
            span.textContent = text;
            const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
            // Use stronger opacity for inline highlights
            span.style.backgroundColor = isDarkTheme ? 'var(--color-collab-added-bg-dark)' : 'var(--color-collab-added-bg)';
            span.style.filter = 'brightness(1.5) saturate(1.5)'; // Make inline highlights more prominent
            span.style.fontWeight = '700';
            span.style.padding = '0 2px';
            span.style.borderRadius = '2px';
            span.style.color = isDarkTheme ? 'var(--color-collab-added-text-dark)' : 'var(--color-collab-added-text)';
            fragment.appendChild(span);
        } else if (operation === -1 && blockType === 'deleted') {
            // Deleted text in a deletion block - make it extra emphasized
            const span = document.createElement('span');
            span.textContent = text;
            const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
            // Use stronger opacity for inline highlights
            span.style.backgroundColor = isDarkTheme ? 'var(--color-collab-removed-bg-dark)' : 'var(--color-collab-removed-bg)';
            span.style.filter = 'brightness(1.5) saturate(1.5)'; // Make inline highlights more prominent
            span.style.fontWeight = '700';
            span.style.padding = '0 2px';
            span.style.borderRadius = '2px';
            fragment.appendChild(span);
        } else if (operation === 0) {
            // Unchanged text
            fragment.appendChild(document.createTextNode(text));
        }
        // Skip opposite operations (deletions in added blocks, additions in deleted blocks)
    });
    
    // Replace the text node with our highlighted version
    parent.replaceChild(fragment, textNode);
}

/**
 * Create a unified diff view
 * @param {Object} specDiff - Specification diff result
 * @param {Array} claimsDiff - Claims diff result
 * @returns {Object} Unified diff for rendering
 */
export function createUnifiedDiff(specDiff, claimsDiff) {
    return {
        spec: {
            changes: specDiff.changes,
            hasChanges: !specDiff.unchanged
        },
        claims: {
            changes: claimsDiff,
            hasChanges: claimsDiff.length > 0
        }
    };
}